import {
  CaretDown,
  CaretRight,
  CheckCircle,
  HandsClapping,
  UsersThree,
} from '@phosphor-icons/react';
import type { Daypart } from '@shh/shared';
import {
  Avatar,
  Button,
  Icon,
  MultiChoice,
  PointsPill,
  ProgressRing,
  Sheet,
  TaskCard,
  Text,
  cn,
  personInk,
  type TaskStatus,
} from '@shh/ui';
import { useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Link } from 'react-router';
import { useLens } from '../app/lens';
import { tickTogether, toggleTask, toggleTodo } from '../data/api';
import { useClock } from '../data/clock';
import {
  currentDaypart,
  familyTodos,
  forAllTasks,
  holderOf,
  isSharedBetween,
  kids,
  memberById,
  stateOf,
  streakOf,
  tasksOf,
  todosOf,
  unitFor,
  type M,
  type T,
} from '../data/derive';
import { useFamily, type FamilyData } from '../data/family';
import { iconFor } from '../lib/icons';
import { useJobEdit } from './JobEdit';
import { tasksText } from '../i18n/tasks';

const toStatus = (s: ReturnType<typeof stateOf>): TaskStatus => s;

/** Would ticking this task complete the person's routine for its part of the day? */
function completesRoutine(f: FamilyData, task: T, memberId: string) {
  if (task.daypart === 'anytime') return false;
  const routine = tasksOf(f, memberId, task.daypart).filter((t) => t.daypart === task.daypart);
  return (
    routine.length > 0 && routine.every((t) => t.id === task.id || stateOf(f, t.id) !== 'open')
  );
}

/**
 * Ticking with the right person. Personal tasks tick for their owner; shared and "für alle"
 * tasks tick for the lens person, the signed-in kid – or ask "Wer hat's gemacht?" on the wall.
 */
export function useTick(onRoutineDone?: (m: M) => void) {
  const f = useFamily();
  const lens = useLens();
  const [ask, setAsk] = useState<{ task: T; candidates: M[] }>();
  const [team, setTeam] = useState<{ task: T; candidates: M[]; selected: string[] }>();

  const run = (task: T, memberId: string) => {
    const finishing = stateOf(f, task.id) === 'open' && completesRoutine(f, task, memberId);
    void toggleTask(f, task, memberId);
    const m = memberById(f, memberId);
    if (finishing && m && m.ageProfile !== 'teen') onRoutineDone?.(m);
  };

  /** `forMemberId`: ticked in that person's column – no need to ask who did it. */
  const tick = (task: T, forMemberId?: string) => {
    const holder = holderOf(f, task.id);
    if (holder) return run(task, holder); // untick by the one who did it
    // "für alle" means everyone – parents take the bins out too (kids first in the picker)
    const eligible = eligibleFor(f, task);
    // Kids/teens with their own login may only tick for themselves (the rules agree).
    const allowed =
      f.role === 'kid' || f.role === 'teen' ? eligible.filter((m) => m.id === f.meId) : eligible;
    const pick =
      allowed.find((m) => m.id === forMemberId) ??
      allowed.find((m) => m.id === lens.id) ??
      allowed.find((m) => m.id === f.meId && f.role !== 'parent') ??
      (allowed.length === 1 ? allowed[0] : undefined);
    if (pick) run(task, pick.id);
    else if (allowed.length) setAsk({ task, candidates: allowed });
  };

  /** "Zusammen gemacht": choose everyone who helped – also after a plain tick. */
  const together = (task: T) => {
    const inst = f.instances[task.id];
    const holder = holderOf(f, task.id);
    const selected = holder
      ? [holder, ...(inst?.with ?? [])]
      : f.meId && f.role !== 'parent'
        ? [f.meId]
        : [];
    setAsk(undefined);
    setTeam({ task, candidates: eligibleFor(f, task), selected });
  };

  const each = team ? Math.ceil(team.task.points / Math.max(1, team.selected.length)) : 0;
  const picker = (
    <>
      <Sheet
        isOpen={!!team}
        onOpenChange={(o) => !o && setTeam(undefined)}
        title={tasksText.whoHelped}
      >
        {team && (
          <div className="flex flex-col gap-gap-lg">
            <Text tone="secondary">{team.task.title}</Text>
            <MultiChoice
              label={tasksText.doneTogether}
              description={tasksText.shareHint}
              layout="tile"
              value={team.selected}
              onChange={(selected) => setTeam({ ...team, selected })}
              choices={team.candidates.map((m) => ({
                value: m.id,
                label: m.name,
                picture: (
                  <Avatar name={m.name} color={m.color} emoji={m.emoji} size="md" decorative />
                ),
              }))}
            />
            {team.selected.length > 0 && (
              <p role="status" className="font-semibold">
                {team.selected.length === 1
                  ? tasksText.starsFor(
                      String(team.task.points),
                      memberById(f, team.selected[0])?.name ?? '',
                    )
                  : tasksText.eachGets(each)}
              </p>
            )}
            <Button
              className="self-end"
              isDisabled={!team.selected.length}
              onPress={() => {
                // keep the one who ticked first; the order of tapping decides otherwise
                const holder = holderOf(f, team.task.id);
                const ordered =
                  holder && team.selected.includes(holder)
                    ? [holder, ...team.selected.filter((id) => id !== holder)]
                    : team.selected;
                void tickTogether(f, team.task, ordered);
                setTeam(undefined);
              }}
            >
              {tasksText.done}
            </Button>
          </div>
        )}
      </Sheet>
      <Sheet
        isOpen={!!ask}
        onOpenChange={(o) => !o && setAsk(undefined)}
        title={tasksText.whoDidIt}
      >
        {ask && (
          <div className="flex flex-col gap-gap-md">
            <Text tone="secondary">{ask.task.title}</Text>
            <ul className="grid grid-cols-3 gap-gap-md">
              {ask.candidates.map((m) => (
                <li key={m.id}>
                  <AriaButton
                    onPress={() => {
                      run(ask.task, m.id);
                      setAsk(undefined);
                    }}
                    className="flex w-full cursor-pointer flex-col items-center gap-gap-xs rounded-card p-inset-xs outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover data-pressed:scale-97"
                  >
                    <Avatar name={m.name} color={m.color} emoji={m.emoji} size="lg" decorative />
                    <span className="font-bold">{m.name}</span>
                  </AriaButton>
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              icon={HandsClapping}
              className="self-start"
              onPress={() => together(ask.task)}
            >
              {tasksText.doneTogetherMore}
            </Button>
          </div>
        )}
      </Sheet>
    </>
  );
  return { tick, together, picker };
}

/** Who may do this task: "für alle" means everyone (kids first), otherwise the assigned people. */
function eligibleFor(f: FamilyData, task: T): M[] {
  return task.assignees === 'all'
    ? [...kids(f), ...f.members.filter((m) => m.role === 'parent')]
    : f.members.filter((m) => (task.assignees as string[]).includes(m.id));
}

function TaskRow({
  task,
  onTick,
  onTogether,
  forMemberId,
}: {
  task: T;
  onTick: (t: T, forMemberId?: string) => void;
  onTogether?: (t: T) => void;
  forMemberId?: string;
}) {
  const f = useFamily();
  const editJob = useJobEdit();
  const holder = holderOf(f, task.id);
  const takenByOther = Boolean(holder && forMemberId && holder !== forMemberId);
  const holderName = memberById(f, holder)?.name;
  const inst = f.instances[task.id];
  const shared = isSharedBetween(task) || task.assignees === 'all';
  const hint = takenByOther
    ? tasksText.faster(holderName ?? '')
    : inst?.status === 'open' && inst.note
      ? inst.note
      : shared && holder
        ? holder === forMemberId
          ? undefined
          : holderName
        : shared
          ? task.assignees === 'all'
            ? tasksText.forAllFirst
            : tasksText.sharedFirst
          : task.repeat.kind === 'once'
            ? tasksText.oneOff
            : task.repeat.kind === 'flex'
              ? tasksText.flex[task.repeat.per]
              : undefined;
  const unit = unitFor(memberById(f, forMemberId ?? holder));
  const helpers = (inst?.with ?? []).map((id) => memberById(f, id)?.name).filter(Boolean);
  // done together is offered for shared tasks once someone ticked them – never in the way
  const offerTogether =
    onTogether && shared && holder && !takenByOther && inst?.status !== 'approved';
  const card = (
    <TaskCard
      density="compact"
      title={task.title}
      icon={iconFor(task.icon)}
      points={task.points}
      pointsUnit={unit}
      accent={memberById(f, forMemberId)?.color ?? 'lagoon'}
      hint={hint}
      streak={shared ? undefined : streakOf(f, forMemberId ?? holder, task)}
      status={toStatus(stateOf(f, task.id))}
      isDisabled={takenByOther || f.instances[task.id]?.status === 'approved'}
      onStatusChange={() => onTick(task, forMemberId)}
      onEdit={editJob({ kind: 'task', task })}
      editLabel={tasksText.editLabel(task.title)}
    />
  );
  if (!offerTogether && !helpers.length) return card;
  return (
    <div className="flex flex-col gap-gap-xs">
      {card}
      <AriaButton
        isDisabled={!offerTogether}
        onPress={() => onTogether?.(task)}
        className="flex min-h-touch-min cursor-pointer items-center gap-gap-xs self-end rounded-control px-inset-xs text-sm font-semibold text-text-secondary outline-none data-disabled:cursor-default data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:text-text-primary"
      >
        <Icon icon={HandsClapping} size="sm" />
        {helpers.length ? tasksText.togetherWith(helpers.join(', ')) : tasksText.togetherAsk}
      </AriaButton>
    </div>
  );
}

interface PersonTasksProps {
  member: M;
  daypart: Daypart | 'all';
  onTick: (t: T, forMemberId?: string) => void;
  onTogether?: (t: T) => void;
  emphasis?: 'normal' | 'focus';
  maxItems?: number;
  showHeader?: boolean;
  /** Only tasks of exactly this daypart (lists grouped by daypart). */
  exactDaypart?: boolean;
  /** Extra content under the list (e.g. a link). */
  footer?: ReactNode;
  /** Dashboard: earlier times of day only show what is still open. */
  focusNow?: boolean;
}

/** Times of day as they read in a sentence – "Morgen" alone sounds like tomorrow. */
const DAYPART_LABEL: Record<Daypart, string> = tasksText.dayparts;
const DAYPARTS: Daypart[] = ['morning', 'afternoon', 'evening', 'anytime'];

/**
 * One person: progress ring, stars and streak, what's open now. Kids get tasks with stars,
 * parents their to-dos – same card, same place, same behaviour.
 */
export function PersonTasks({
  member,
  daypart,
  onTick,
  onTogether,
  emphasis = 'normal',
  maxItems = 4,
  showHeader = true,
  exactDaypart = false,
  footer,
  focusNow = false,
}: PersonTasksProps) {
  const f = useFamily();
  const editJob = useJobEdit();
  const isParent = member.role === 'parent';
  const unit = unitFor(member);
  // parents have tasks too since 1.9 (their stars go to the family goal) – and their to-dos
  const all = tasksOf(f, member.id);
  const list = daypart === 'all' ? all : tasksOf(f, member.id, daypart, exactDaypart);
  const todos = isParent ? todosOf(f, member.id) : [];
  const tasksDone = all.filter((t) => stateOf(f, t.id) !== 'open').length;
  const done = tasksDone + todos.filter((t) => t.done).length;
  const total = all.length + todos.length;
  const allDone = total > 0 && done === total;
  const allTasksDone = all.length > 0 && tasksDone === all.length;
  // "Alles geschafft" hid the list – a tick by mistake could not be undone (Mike, 04.10.)
  const [reviewing, setReviewing] = useState(false);
  // open first, then the rest – so the next thing to do is always on top
  const now = currentDaypart(useClock(f.timeZone));
  // one card per person, the day grouped inside: open first within each time of day
  const openFirst = (ts: T[]) => [
    ...ts.filter((t) => stateOf(f, t.id) === 'open'),
    ...ts.filter((t) => stateOf(f, t.id) !== 'open'),
  ];
  const candidates = (daypart === 'all' ? DAYPARTS : [daypart]).map((d) => {
    const past = focusNow && d !== 'anytime' && DAYPARTS.indexOf(d) < DAYPARTS.indexOf(now);
    return {
      d,
      items: openFirst(daypart === 'all' ? list.filter((t) => t.daypart === d) : list).filter(
        (t) => !past || stateOf(f, t.id) === 'open',
      ),
    };
  });
  // at most maxItems in total, in the order of the day
  const groups = candidates
    .map((g, i) => {
      const before = candidates.slice(0, i).reduce((n, c) => n + c.items.length, 0);
      return { d: g.d, items: g.items.slice(0, Math.max(0, maxItems - before)) };
    })
    .filter((g) => g.items.length);
  const ordered = groups.flatMap((g) => g.items);
  const taskList = (
    <div className="flex flex-col gap-gap-sm">
      {groups.map((g) => (
        <div key={g.d} className="flex flex-col gap-gap-xs">
          {daypart === 'all' && (
            <span
              className={cn(
                'text-xs font-bold uppercase tracking-wide',
                g.d === now ? 'text-accent-primary' : 'text-text-muted',
              )}
            >
              {DAYPART_LABEL[g.d]}
              {g.d === now ? tasksText.now : ''}
            </span>
          )}
          <ul className="flex flex-col gap-gap-xs">
            {g.items.map((t) => (
              <li key={t.id}>
                <TaskRow task={t} onTick={onTick} onTogether={onTogether} forMemberId={member.id} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const review = (
    <Button
      variant="quiet"
      aria-expanded={reviewing}
      onPress={() => setReviewing((r) => !r)}
      className="self-start"
    >
      <Icon
        icon={CaretDown}
        size="sm"
        weight="bold"
        className={cn('transition', reviewing && 'rotate-180')}
      />
      {reviewing ? tasksText.hide : tasksText.showDone}
    </Button>
  );
  const dayList = (
    <ul aria-label={tasksText.doneBy(member.name)} className="flex flex-col gap-gap-xs">
      {DAYPARTS.flatMap((d) => all.filter((t) => t.daypart === d)).map((t) => (
        <li key={t.id}>
          <TaskRow task={t} onTick={onTick} onTogether={onTogether} forMemberId={member.id} />
        </li>
      ))}
    </ul>
  );

  return (
    <article
      aria-label={tasksText.progress(member.name, String(done), String(total))}
      className={cn(
        'flex flex-col gap-gap-md rounded-card border-(length:--shh-card-border) border-border-card bg-surface-raised p-inset-sm shadow-card',
        emphasis === 'focus' && 'ring-3 ring-accent-secondary',
      )}
    >
      {showHeader && (
        <header className="flex items-center gap-gap-sm">
          <Avatar
            name={member.name}
            color={member.color}
            emoji={member.emoji}
            size="md"
            decorative
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <Link
              to={`/person/${member.id}`}
              className={cn(
                'inline-flex items-center gap-gap-xs self-start rounded-control font-display text-lg font-black leading-tight outline-none hover:underline focus-visible:outline-3 focus-visible:outline-border-focus',
                personInk[member.color],
              )}
            >
              {member.name}
              <Icon icon={CaretRight} size="sm" weight="bold" />
            </Link>
            {!isParent && (
              <span className="flex flex-wrap items-center gap-x-gap-sm text-sm">
                <PointsPill points={member.balance} unit={unit} />
              </span>
            )}
            {/* parents: no balance to spend – their stars grow the family goal */}
            {isParent && all.length > 0 && (
              <span className="text-sm text-text-secondary">{tasksText.starsForGoal}</span>
            )}
            {/* nothing to do: say it in the header – the card stays one line high */}
            {isParent && !total && (
              <span className="text-sm text-text-muted">{tasksText.noTodos}</span>
            )}
          </div>
          {total > 0 && (
            <ProgressRing
              value={done}
              max={total}
              tone={allDone ? 'success' : 'points'}
              label={tasksText.todayOf(member.name)}
              valueLabel={tasksText.doneOf(String(done), String(total))}
              className="size-avatar-md"
            >
              <span className="text-xs">
                {done}/{total}
              </span>
            </ProgressRing>
          )}
        </header>
      )}

      {isParent ? (
        <>
          {allTasksDone ? (
            <>
              <p className="flex items-center gap-gap-sm text-sm font-bold text-feedback-success">
                <Icon icon={CheckCircle} size="sm" weight="fill" />
                {tasksText.parentDone}
              </p>
              {review}
              {reviewing && dayList}
            </>
          ) : (
            ordered.length > 0 && taskList
          )}
          {todos.length > 0 && (
            <ul aria-label={tasksText.todos} className="flex flex-col gap-gap-xs">
              {todos.slice(0, maxItems).map((t) => (
                <li key={t.id}>
                  <TaskCard
                    density="compact"
                    title={t.title}
                    icon={iconFor(t.icon)}
                    hint={
                      t.assignees !== 'all' && t.assignees.length > 1
                        ? tasksText.together
                        : t.due === f.today
                          ? tasksText.today
                          : undefined
                    }
                    accent={member.color}
                    status={t.done ? 'done' : 'open'}
                    onStatusChange={() => void toggleTodo(f.householdId, t, member.id)}
                    onEdit={editJob({ kind: 'todo', todo: t })}
                    editLabel={tasksText.editLabel(t.title)}
                  />
                </li>
              ))}
            </ul>
          )}
          {!total && !showHeader && (
            <Text size="sm" tone="muted">
              {tasksText.noTodos}
            </Text>
          )}
        </>
      ) : allDone ? (
        <>
          <p className="flex items-center gap-gap-sm rounded-control bg-feedback-success-surface p-inset-sm font-bold text-feedback-success">
            <Icon icon={CheckCircle} size="md" weight="fill" />
            {tasksText.allDone}
          </p>
          {all.length > 0 && review}
          {reviewing && dayList}
        </>
      ) : ordered.length ? (
        taskList
      ) : (
        <Text size="sm" tone="muted">
          {tasksText.nothingNow}
        </Text>
      )}
      {footer}
    </article>
  );
}

/** "Für alle": household to-dos and tasks anyone may take (stars for the first one). */
export function ForEveryone({
  onTick,
  onTogether,
}: {
  onTick: (t: T) => void;
  onTogether?: (t: T) => void;
}) {
  const f = useFamily();
  const editJob = useJobEdit();
  const lens = useLens();
  const todos = familyTodos(f);
  const tasks = forAllTasks(f);
  if (!todos.length && !tasks.length) return null;
  return (
    <article className="flex flex-col gap-gap-md rounded-card bg-accent-secondary-soft p-inset-sm">
      <header className="flex items-center gap-gap-sm">
        <Icon icon={UsersThree} size="md" weight="fill" className="text-accent-secondary" />
        <span className="font-display text-lg font-black">{tasksText.forAll}</span>
        <span className="text-sm text-text-secondary">{tasksText.firstComes}</span>
      </header>
      <ul className="flex flex-col gap-gap-xs">
        {todos.map((t) => (
          <li key={t.id}>
            <TaskCard
              density="compact"
              title={t.title}
              icon={iconFor(t.icon)}
              accent="lagoon"
              hint={t.done ? memberById(f, t.doneBy)?.name : undefined}
              status={t.done ? 'done' : 'open'}
              onStatusChange={() => void toggleTodo(f.householdId, t, lens.member?.id ?? f.meId)}
              onEdit={editJob({ kind: 'todo', todo: t })}
              editLabel={tasksText.editLabel(t.title)}
            />
          </li>
        ))}
        {tasks.map((t) => (
          <li key={t.id}>
            <TaskRow task={t} onTick={onTick} onTogether={onTogether} />
          </li>
        ))}
      </ul>
    </article>
  );
}
